export interface FollowControllerConfig {
  /** Dead zone fraction of focus height (default 0.35 = ±35%) */
  deadZoneFraction?: number;
  /** Maximum tracking velocity in px/s (default 450 px/s) */
  maxVelocity?: number;
  /** Maximum acceleration in px/s^2 (default 800 px/s^2) */
  maxAcceleration?: number;
  /** Spring/damping constant for critically-damped feel */
  dampingFactor?: number;
  /** Manual override suspension duration in ms (default 2000ms) */
  overrideSuspendDurationMs?: number;
}

export class FollowController {
  private targetY: number | null = null;
  private currentVelocity = 0.0;
  private isSuspended = false;
  private suspendUntilMs = 0;

  private readonly deadZoneFraction: number;
  private readonly maxVelocity: number;
  private readonly maxAcceleration: number;
  private readonly dampingFactor: number;
  private readonly overrideSuspendDurationMs: number;

  constructor(config: FollowControllerConfig = {}) {
    this.deadZoneFraction = config.deadZoneFraction ?? 0.35;
    this.maxVelocity = config.maxVelocity ?? 450.0;
    this.maxAcceleration = config.maxAcceleration ?? 800.0;
    this.dampingFactor = config.dampingFactor ?? 4.0;
    this.overrideSuspendDurationMs = config.overrideSuspendDurationMs ?? 2000.0;
  }

  /**
   * Updates target position from speech alignment
   */
  setTargetY(targetY: number | null): void {
    this.targetY = targetY;
  }

  /**
   * Signal user manual input (wheel, touch, arrow keys).
   * Suspends automated following for ~2 seconds.
   */
  notifyManualInput(nowMs: number): void {
    this.isSuspended = true;
    this.suspendUntilMs = nowMs + this.overrideSuspendDurationMs;
    this.currentVelocity = 0.0;
  }

  isOverrideActive(nowMs: number): boolean {
    if (this.isSuspended) {
      if (nowMs >= this.suspendUntilMs) {
        this.isSuspended = false;
        return false;
      }
      return true;
    }
    return false;
  }

  /**
   * Calculates velocity to supply to the scroll engine for this frame.
   * @param currentY Current scroll y of the engine
   * @param focusHeightPx Height of focus band in px
   * @param confidence Alignment confidence (0..1)
   * @param dtSeconds Delta time of the frame
   * @param nowMs Current timestamp
   */
  update(
    currentY: number,
    focusHeightPx: number,
    confidence: number,
    dtSeconds: number,
    nowMs: number
  ): number {
    // 1. If user manual override active, velocity is 0
    if (this.isOverrideActive(nowMs)) {
      this.currentVelocity = 0.0;
      return 0.0;
    }

    // 2. Confidence gating: freeze target and ramp velocity to 0 if low confidence
    if (this.targetY === null || confidence < 0.35 || dtSeconds <= 0) {
      const decel = this.maxAcceleration * dtSeconds;
      if (this.currentVelocity > 0) {
        this.currentVelocity = Math.max(0, this.currentVelocity - decel);
      } else if (this.currentVelocity < 0) {
        this.currentVelocity = Math.min(0, this.currentVelocity + decel);
      }
      return this.currentVelocity;
    }

    // 3. Error distance
    const errorY = this.targetY - currentY;
    const deadZone = focusHeightPx * this.deadZoneFraction;

    // 4. Dead zone check: within ±35% of focus height, hold steady
    if (Math.abs(errorY) <= deadZone) {
      const decel = this.maxAcceleration * dtSeconds;
      if (this.currentVelocity > 0) {
        this.currentVelocity = Math.max(0, this.currentVelocity - decel);
      } else if (this.currentVelocity < 0) {
        this.currentVelocity = Math.min(0, this.currentVelocity + decel);
      }
      return this.currentVelocity;
    }

    // Effective error beyond dead zone boundary
    const effectiveError = errorY > 0 ? errorY - deadZone : errorY + deadZone;

    // 5. Critically-damped desired velocity
    let desiredVelocity = effectiveError * this.dampingFactor;

    // Clamp desired velocity to max velocity
    desiredVelocity = Math.max(-this.maxVelocity, Math.min(this.maxVelocity, desiredVelocity));

    // 6. Acceleration limiting
    const maxDeltaV = this.maxAcceleration * dtSeconds;
    const deltaV = desiredVelocity - this.currentVelocity;
    const clampedDeltaV = Math.max(-maxDeltaV, Math.min(maxDeltaV, deltaV));

    this.currentVelocity += clampedDeltaV;
    return this.currentVelocity;
  }
}
